<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <h4>
                    {{ $t('org.dashboard.Weekly Cost') }}
                </h4>
            </div>
            <b-card-actions-container
                v-if="!editMode && data.days.length &&  !viewTypeIsMobile"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>

        <div
            id="tour-dashboard-step-2"
            class="d-flex position-absolute"
            :style="{
                lineHeight: '36px',
                height: '36px',
                marginLeft: '1.7rem',
                width: `calc(50px * ${data.yKeys.length})`,
                backgroundColor: 'transparent',
            }"
        >
        </div>
        <LineChart
            class="dashboard-chart"
            :data="chartData"
            :options="chartOptions"
            :styles="fetchedChartResponsiveStyle"
        />
        <div
            ref="REF_COLLAPSE"
            :style="collapseResponsiveStyle"
        >
            <CardWeeksTable
                v-show="!editMode && isVisible"
                :data="data"
                class="w-100 m-0"
            />
        </div>
    </b-overlay>
</template>

<script>
import BCardActionsContainer from '@/components/BCardActionsContainer.vue'
import CreateVueChart from '@/components/CreateVueChart.js'
import CardWeeksTable from './CardWeeksTable'
import useCommonData from '@/composables/useCommonData'
import useDashboard from '@/composables/useDashboard'
import useXAxisData from '@/composables/useXAxisData'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { Chart } from 'chart.js'
import { getCurrentInstance, computed, watch, ref, toRefs, nextTick, inject } from '@vue/composition-api'
import useDashboardRender from '@/composables/useDashboardRender'

export default {
    props: ['editMode', 'isOpen'],
    components: {
        CardWeeksTable,
        BCardActionsContainer,
        LineChart: CreateVueChart('Line')
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const isVisible = ref(false)
        const $themeColors = inject('themeColors')
        const { isOpen } = toRefs(props)
        const { t } = useI18nUtils()
        const { getSummaryWeeks } = useDashboard()
        const {
            viewType
        } = useDashboardRender()
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const REF_COLLAPSE = ref(null)
        const { __getOrdinal, __getMonthShortString, __getDollar, __toKFormat, __getDayString } = useCommonData()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, data] = getSummaryWeeks(currentParams.value)
        const { createDays } = useXAxisData()
        const colors = [$themeColors.value.primary, '#FFE802', $themeColors.value.warning, '#299AFF', '#28DAC6', '#EC6567']
        const chartResponsiveStyle = ref({
            position: 'absolute',
            top: '62px',
            bottom: '0',
            left: '0',
            right: '0',
            'margin': '0rem 1.7rem 1.5rem 1.7rem'
        })
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }
        const chartData = computed(() => {
            const labels = data.value.days.length ? data.value.days.map(day => __getDayString(day)) : createDays()
            let datasets = data.value.yKeys.map((week, index) => ({
                mode: 'index',
                type: 'line',
                fill: false,
                label: __getOrdinal(week),
                borderCapStyle: 'round',
                borderColor: colors[index % colors.length],
                pointBorderColor: colors[index % colors.length],
                pointBackgroundColor: colors[index % colors.length],
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: colors[index % colors.length],
                data: data.value.weeklyCost[week],
                lineTension: 0.3,
                borderWidth: 2,
                pointRadius: 0
            }))
            if (!data.value.yKeys.length) {
                datasets = colors.map(color => ({
                    mode: 'index',
                    type: 'line',
                    fill: false,
                    label: '-      ',
                    borderCapStyle: 'round',
                    borderColor: color,
                    pointBorderColor: color,
                    pointBackgroundColor: color,
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: color,
                    lineTension: 0.3,
                    borderWidth: 2,
                    pointRadius: 0
                }))
            }

            return {
                labels,
                datasets
            }
        })

        const chartOptions = computed(() => ({
            tooltips: {
                intersect: false,
                callbacks: {
                    title: tooltipItem => tooltipItem[0].label,
                    label: tooltipItem => `${__getOrdinal(data.value.yKeys[tooltipItem.datasetIndex])}: ${__getDollar(tooltipItem.value)}`
                }
            },
            layout: {
                padding: 0
            },
            legend: {
                display: true,
                labels: {
                    usePointStyle: true,
                    padding: 12,
                    boxWidth: 5,
                    fontSize: 11
                },
                position: 'top',
                align: 'start'
            },
            scales: {
                yAxes: [
                    {
                        display: true,
                        gridLines: {
                            zeroLineColor: '#dbdbdb',
                            color: '#dbdbdb'
                        },
                        ticks: {
                            callback: label => __toKFormat(label, 2),
                            // beginAtZero: true,
                            count: 6,
                            maxTicksLimit: 6,
                            padding: 8
                        }
                    }
                ],
                xAxes: [
                    {
                        ticks: {
                            padding: 8
                        },
                        gridLines: {
                            color: '#dbdbdb',
                            zeroLineColor: '#dbdbdb'
                        }
                    }
                ]
            },
            plugins: {
                noDataBackground: {
                    display: true,
                    imageSrc: require('@/assets/images/empty.png'),
                    text: t('org.dashboard.No data')
                },
                datalabels: {
                    display: false
                }
            }
        }))

        Chart.Legend.prototype.afterFit = function () {
            this.height = this.height + 12
        }
        watch(currentParams, (value, oldValue) => {
            getSummaryWeeks(value)
        }, {
            deep: true
        })
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'card-weeks', status })
        }
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            nextTick(() => {
                const tableHeight = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                chartResponsiveStyle.value.bottom = value ? `${tableHeight}px` : 0
            })
        })
        const fetchedChartResponsiveStyle = computed(() => chartResponsiveStyle.value)
        return {
            __getMonthShortString,
            __getDollar,
            __getOrdinal,
            __toKFormat,
            colors,
            status,
            chartData,
            chartOptions,
            triggerCollapse,
            isVisible,
            data,
            REF_COLLAPSE,
            collapseResponsiveStyle,
            fetchedChartResponsiveStyle,
            viewTypeIsMobile
        }
    }
}
</script>

<style>

</style>
